import { QueryClient, useQueries, useQuery } from "@tanstack/react-query";

const contactSmallV2CacheKey = (contactId: number) => ["contact-small-v2", contactId];
const contactCacheKey = (contactId: number) => ["contact", contactId];

const getContactCacheKey = (contact: server.dto.ContactSmallV2 | server.dto.Contact) => {
  if (contact == null) return ["contact-undefined"]
  if ("passport" in contact) return contactCacheKey(contact.contactId);
  return contactSmallV2CacheKey(contact.contactId);
}

export const invalidateContactCache = (queryClient: QueryClient, contactId: number) => {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: contactSmallV2CacheKey(contactId) }),
    queryClient.invalidateQueries({ queryKey: contactCacheKey(contactId) }),
    queryClient.invalidateQueries({ queryKey: ["linked-contacts-to-company"] })
  ])
};

const FIVE_MIN = 5 * 60 * 1000;

const getQueryParams = <T extends server.dto.ContactSmallV2 | server.dto.Contact>(contact: T) => {
  return {
    queryKey: getContactCacheKey(contact),
    queryFn: () => contact != null
      ? window.CoreApi.Contact.Contact(contact.contactId).then(r => r.data.data)
      : Promise.resolve(null),

    // Only used if cache is empty for this key; otherwise ignored.
    initialData: contact as unknown as server.dto.Contact,
    staleTime: FIVE_MIN
  }
};

export function useContacts<T extends server.dto.ContactSmallV2 | server.dto.Contact>(
  contacts: readonly T[] | undefined
) {
  const list = contacts ?? [];
  const queries = useQueries({
    queries: list.map(contact => getQueryParams(contact))
  });

  return queries.map(q => q.data) as T[];
}

/**
 * Hook that syncs contact data with the server
 * If the contact preview panel modifies a contact, this hook will return the updated contact data
 * @param contact Initial contact data. If the cache is empty, this data will be used as initial cache data. 
 * @returns 
 */
export const useContact = <T extends server.dto.ContactSmallV2 | server.dto.Contact>(contact: T) => {
  const contactQuery = useQuery(getQueryParams(contact));

  return contactQuery.data as T
}